import { Button, Icon, ResponsiveDrawer, TextField, Typography, getContrastColor } from '@fox/atoms';
import { FlexColumnSmallSpaced, FlexColumnSpaced } from '@fox/layout';
import { useMemo, useState } from 'react';
import styled from 'styled-components';
import { useContentContext } from '../../content-context';
import { useSwiftFoxTenantConfigContext } from '../../swiftfox-tenant-config-context';
import { useEventListContext } from './event-list-context';
import { translateDate } from './filters';
import { DateFilterOptions, LocationFilterOptions } from './util';

const ChipWrapper = styled.div`
	display: flex;
	flex-wrap: wrap;
	gap: 1rem;
`;

const ChipItem = styled.div<{ isActive: boolean }>`
	border-radius: 30px;
	padding: 0.25rem 0.5rem;
	width: max-content;
	gap: 1rem;
	display: flex;
	align-items: center;
	transition: background-color 0.2s ease-in-out;
	background-color: ${props => (props.isActive ? props.theme.colors.primary : props.theme.colors.grey[200])};
	svg {
		opacity: ${props => (props.isActive ? 1 : 0.6)};
	}
	${props => (props.isActive ? `color: ${getContrastColor(props.theme.colors.primary)};` : '')}
`;

export function MobileFilters() {
	const [drawerOpen, setDrawerOpen] = useState<boolean>(false);
	const { addressLocalisations } = useSwiftFoxTenantConfigContext();
	const stateFilterOptions = addressLocalisations.states;
	const { isLoggedIn } = useContentContext();
	const {
		rsvpOptions,
		locationFilter,
		stateFilter,
		dateFilter,
		rsvpFilter,
		setRsvpFilter,
		setDateFilter,
		setLocationFilter,
		setStateFilter,
		setSelectedPage,
		setStartDate,
		setEndDate,
		search,
		setSearch
	} = useEventListContext();
	const chips = useMemo(
		() =>
			[
				...Object.values(LocationFilterOptions).map(o => ({
					updateFunc: (value: string, add: boolean) => {
						if (add) {
							setLocationFilter(prev => [...prev, value]);
						} else {
							setLocationFilter(prev => prev.filter(l => l !== value));
						}
						setSelectedPage(0);
					},
					value: o,
					displayName: o,
					isActive: locationFilter?.includes(o)
				})),
				...Object.values(stateFilterOptions).map(o => ({
					updateFunc: (value: string, add: boolean) => {
						if (add) {
							setStateFilter(prev => [...prev, value]);
						} else {
							setStateFilter(prev => prev.filter(l => l !== value));
						}
						setSelectedPage(0);
					},
					value: o,
					displayName: o,
					isActive: stateFilter?.includes(o)
				})),
				...Object.values(DateFilterOptions).map(o => ({
					updateFunc: (value: string, add: boolean) => {
						if (add) {
							const { startDate, endDate } = translateDate(value);
							setStartDate(startDate);
							setEndDate(endDate);
							setDateFilter(value);
						} else {
							setDateFilter('All events');
							setStartDate(undefined);
							setEndDate(undefined);
						}
						setSelectedPage(0);
					},
					value: o,
					displayName: o,
					isActive: dateFilter?.includes(o)
				})),
				...(isLoggedIn ? rsvpOptions ?? [] : []).map(o => {
					return {
						updateFunc: (value: string, add: boolean) => {
							if (add) {
								setRsvpFilter(prev => [...prev, value]);
							} else {
								setRsvpFilter(prev => prev.filter(l => l !== value));
							}
							setSelectedPage(0);
						},
						value: o.id,
						displayName: o.displayName,
						isActive: rsvpFilter?.includes(o.id)
					};
				})
			] as {
				updateFunc: (value: string, add: boolean) => void;
				value: string;
				displayName: string;
				isActive: boolean;
			}[],
		[rsvpOptions, locationFilter, dateFilter, rsvpFilter, stateFilter]
	);
	return (
		<FlexColumnSpaced style={{ padding: '1rem' }}>
			<TextField
				value={search ?? ''}
				onChange={e => setSearch(e.target.value)}
				sx={{ width: '100%' }}
				size="small"
				placeholder="Search"
				endAdornment={<Icon iconName="Search" />}
				inputProps={{
					style: { paddingLeft: 10, paddingRight: 15 }
				}}
			/>
			<Button style={{ maxWidth: 'max-content' }} variant="secondary" onClick={() => setDrawerOpen(true)}>
				See filters
			</Button>
			<ResponsiveDrawer
				headerVariant="h5"
				placement="bottom"
				title={'Event filters'}
				width="100%"
				height="100%"
				style={{ height: '100%' }}
				open={drawerOpen}
				destroyOnClose
				onClose={() => {
					setDrawerOpen(false);
				}}
			>
				<FlexColumnSmallSpaced style={{ padding: '1rem' }}>
					<Typography>To apply filter, click on chips. You can remove filter by clicking the chip again.</Typography>
					<ChipWrapper>
						{chips?.map(chip => {
							return (
								<ChipItem
									isActive={chip.isActive}
									key={chip.value}
									onClick={() => {
										chip.updateFunc(chip.value, !chip.isActive);
									}}
								>
									{chip.displayName}
									<Icon iconName={chip.isActive ? 'Cancel' : 'Add'} />
								</ChipItem>
							);
						})}
					</ChipWrapper>
				</FlexColumnSmallSpaced>
			</ResponsiveDrawer>
		</FlexColumnSpaced>
	);
}
